import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../store/redux/hooks'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { StyledPageContent } from '../../styles/PageStyles'
import { Box, Button, Collapse, Stack, useMediaQuery, useTheme } from '@mui/material'
import { StyledButtonPrimary } from '../../styles/ButtonStyles'
import { Add, FilterList } from '@mui/icons-material'
import navigation from '../../config/navigation'
import { useNavigate } from 'react-router-dom'
import defaultProps from '../../config/defaultProps'
import { RequestProps } from '../../store/types'
import { useGetCandidatesQuery } from '../../store/redux/reducers/candidateSlice'
import FilterCandidatesMenu from '../../components/FilterMenu/Candidates/FilterCandidatesMenu'
import Searchbar from '../../components/Searchbar/Searchbar'
import { splitName } from '../../store/utils/usersUtils'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import CandidatesKanbanBoard from './KanbanBoard/CandidatesKanbanBoard'
import { GeneralCandidate } from '../../store/types/models/GeneralCandidate'
import { useTranslation } from 'react-i18next'
import HeaderCountLabel from '../../components/Page/HeaderCountLabel'

const CandidatesView = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const theme = useTheme()
  const [isFilteringMenuOpen, setIsFilteringMenuOpen] = useState(false)
  const [candidatesCount, setCandidatesCount] = useState<number>(0)
  const [requestProps, setRequestProps] = useState<RequestProps>({ ...defaultProps, size: 500 })
  const { data: candidatesResponse, isSuccess } = useGetCandidatesQuery(requestProps)
  const [candidates, setCandidates] = useState<GeneralCandidate[]>([])
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isUpLg = useMediaQuery(theme.breakpoints.up('lg'))

  useEffect(() => {
    if (candidatesResponse && isSuccess) {
      const sortedCandidates = candidatesResponse.content.map(candidate => ({
        ...candidate,
        technologies: [...candidate.technologies].sort((a, b) => b.level - a.level),
      }))

      setCandidates(sortedCandidates)
      setCandidatesCount(candidatesResponse.totalElements)
    }
  }, [candidatesResponse, isSuccess])

  useEffect(() => {
    dispatch(setPageName(t('page.name.candidates.candidates')))
    dispatch(setPageIndex(PageIndex.CANDIDATES))
    dispatch(
      pageHeaderActions.setButtons({
        monthPicker: false,
        infoButton: false,
      })
    )
  }, [dispatch, t])

  const handleSearch = (value: string) => {
    const { firstName, lastName } = splitName(value)

    setRequestProps(prevProps => ({
      ...prevProps,
      firstName: lastName ? firstName : undefined,
      lastName: lastName || undefined,
      'query:firstName,lastName': lastName ? undefined : firstName,
      page: 0,
    }))
  }

  return (
    <StyledPageContent display={'flex'} flexDirection={'column'} gap={2} color="text.light">
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
        <HeaderCountLabel label={t('components.subheader.candidates')} count={candidatesCount} />
        {isDownSm && (
          <StyledButtonPrimary
            variant="contained"
            startIcon={<Add />}
            size="medium"
            onClick={() => navigate(navigation.newCandidate)}
          >
            {t('components.button.addCandidateShort')}
          </StyledButtonPrimary>
        )}
      </Box>
      <Stack
        direction={isDownSm ? 'column-reverse' : 'row'}
        sx={{ justifyContent: 'space-between', alignItems: 'end', gap: 2 }}
      >
        <Button
          variant="contained"
          color="secondary"
          startIcon={<FilterList />}
          className={isFilteringMenuOpen ? 'selected' : ''}
          onClick={() => setIsFilteringMenuOpen(!isFilteringMenuOpen)}
          fullWidth={isDownSm}
        >
          {t('page.users.buttons.filter')}
        </Button>
        {isUpLg && <Searchbar placeholder={t('components.placeholder.search')} handleChange={handleSearch} />}
        {!isDownSm && (
          <StyledButtonPrimary
            variant="contained"
            startIcon={<Add />}
            size="medium"
            onClick={() => navigate(navigation.newCandidate)}
          >
            {t('components.button.addCandidate')}
          </StyledButtonPrimary>
        )}
        {!isUpLg && <Searchbar placeholder={t('components.placeholder.search')} handleChange={handleSearch} />}
      </Stack>
      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        gap={isFilteringMenuOpen ? 2 : 0}
        height="fit-content"
        sx={{ paddingBottom: 3 }}
      >
        {isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation="vertical" unmountOnExit>
            <Box width={'100%'}>
              <FilterCandidatesMenu requestProps={requestProps} setRequestProps={setRequestProps} />
            </Box>
          </Collapse>
        )}

        {!isDownMd && (
          <Collapse in={isFilteringMenuOpen} orientation="horizontal" unmountOnExit>
            <Box width={'fit-content'}>
              <FilterCandidatesMenu requestProps={requestProps} setRequestProps={setRequestProps} />
            </Box>
          </Collapse>
        )}

        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', p: 0.5, margin: -0.5 }}>
          <CandidatesKanbanBoard candidates={candidates} />
        </Box>
      </Box>
    </StyledPageContent>
  )
}

export default CandidatesView
